import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { Button, Typography, useMutationWithToast } from '@pesto/ui';
import { ControlledAlertDialog } from '@pesto/ui/components/AlertDialog/AlertDialogComponent';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { getCurrentDate } from '@pesto/utils';
import { isValid } from 'date-fns';
import { parseISO } from 'date-fns/parseISO';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import { LoanClosureReason, LoanStatus, useCloseLoanMutation } from '../../__generated__/graphql/api';
import { getLoanMutationRefreshOptions } from '../../utils/loanCacheInvalidation';

const schema = z.object({
  reason: z.nativeEnum(LoanClosureReason),
  effectiveDate: z
    .string()
    .min(1, 'Effective date is required')
    .refine((value) => isValid(parseISO(value)), 'Invalid date format'),
  requestedAt: z
    .string()
    .optional()
    .refine((value) => !value || isValid(parseISO(value)), 'Invalid date format'),
});

type CloseLoanForm = z.infer<typeof schema>;

const closureReasonOptions = Object.values(LoanClosureReason)
  .filter((reason) => reason === LoanClosureReason.PaidOff)
  .map((value) => ({ value, label: 'Paid off' }));

export const CloseLoanComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const [closeLoan, { loading }] = useCloseLoanMutation(getLoanMutationRefreshOptions(loanId));
  const [pendingClosure, setPendingClosure] = useState<CloseLoanForm>();

  const handleCloseLoan = () => {
    if (!pendingClosure) return;

    void handleMutation({
      mutation: () =>
        closeLoan({
          variables: {
            loanId,
            effectiveDate: pendingClosure.effectiveDate,
            reason: pendingClosure.reason,
            requestedAt: pendingClosure.requestedAt || undefined,
          },
        }),
      extractSuccess: (result) => result?.data?.closeLoan?.status === LoanStatus.Closed,
      onSuccess: () => api.close(),
      debugLabel: 'close-loan',
      toast: {
        success: 'Loan closed successfully',
        errorToast: () => ({
          title: 'Failed to close loan',
          description: 'The loan was not closed. Review the details and try again.',
        }),
        loading: 'Closing loan...',
      },
    });
  };

  const config: SmartFormConfig<CloseLoanForm> = {
    fields: [
      {
        name: 'reason',
        label: 'Closure Reason',
        type: 'select',
        required: true,
        options: closureReasonOptions,
      },
      {
        name: 'effectiveDate',
        label: 'Effective Date',
        type: 'date',
        required: true,
        helperText: 'Date the paid-off closure takes effect.',
      },
      {
        name: 'requestedAt',
        label: 'Closure Requested Date',
        type: 'date',
        helperText: 'Original date the servicer requested closure, if provided.',
      },
    ],
    schema,
    defaultValues: {
      reason: LoanClosureReason.PaidOff,
      effectiveDate: getCurrentDate(),
      requestedAt: '',
    },
  };

  return (
    <div className="space-y-5">
      <div className="border-destructive/30 bg-destructive/5 flex gap-3 rounded-lg border p-4">
        <AlertTriangle className="text-destructive mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="space-y-1">
          <Typography variant="headerSmall" className="text-destructive font-semibold">
            This action is permanent
          </Typography>
          <Typography variant="bodySmall" className="text-muted-foreground">
            Closing this loan cannot be undone. Review the closure details before continuing.
          </Typography>
        </div>
      </div>

      <div className="bg-muted/40 space-y-2 rounded-lg border p-4">
        <div className="flex items-center justify-between gap-4">
          <Typography variant="bodySmall" className="text-muted-foreground">
            Loan ID
          </Typography>
          <Typography variant="bodySmall" className="truncate font-mono font-medium">
            {loanId}
          </Typography>
        </div>
      </div>

      <SmartForm
        config={config}
        onSubmit={setPendingClosure}
        loading={loading}
        bottomContent={() => (
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={() => api.cancel()} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" color="destructive" loading={loading}>
              Close Loan
            </Button>
          </div>
        )}
      />

      <ControlledAlertDialog
        isOpen={Boolean(pendingClosure)}
        onClose={() => setPendingClosure(undefined)}
        title="Close this loan permanently?"
        description={`Loan ${loanId} will be closed as paid off. This action cannot be undone.`}
        confirmText="Yes, Close Loan"
        onConfirm={handleCloseLoan}
        variant="destructive"
      />
    </div>
  );
};
